import { Injectable, inject } from '@angular/core';
import { ApiService } from '../api/api.service';
import { API_CONFIG } from '../config/api.config';
import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

export interface LookupRecord {
    ROWID: string;
    NAME: string;
    CODE?: string;
    SYMBOL?: string;
}

function parseJsonLike(value: unknown): any {
    if (typeof value !== 'string') return value;
    const raw = value.trim();
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch {
        return value;
    }
}

function isUuidLike(value: unknown): string {
    const raw = String(value || '').trim();
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(raw) ? raw : '';
}

function extractUploadFileId(response: any): string {
    const normalized = parseJsonLike(response);
    if (!normalized) return '';

    const directCandidates = [
        normalized?.file_id,
        normalized?.fileId,
        normalized?.FILE_ID,
        normalized?.ROWID,
        normalized?.rowId
    ];
    for (const candidate of directCandidates) {
        const value = isUuidLike(candidate);
        if (value) return value;
    }

    const nestedKeys = ['data', 'body', 'content', 'result', 'payload', 'record', 'response', 'file'];
    for (const key of nestedKeys) {
        const nested = normalized?.[key];
        const value = extractUploadFileId(nested);
        if (value) return value;
    }

    if (Array.isArray(normalized)) {
        for (const entry of normalized) {
            const value = extractUploadFileId(entry);
            if (value) return value;
        }
    }

    return '';
}

@Injectable({
    providedIn: 'root'
})
export class PropertyUploadService {
    private api = inject(ApiService);

    private buildUploadFormData(
        file: File,
        options: {
            isPublic?: boolean;
            owner?: 'org' | 'user';
            context?: 'property' | 'project';
            propertyId?: string | null;
            projectId?: string | null;
            orgId?: string | null;
        } = {}
    ): FormData {
        const formData = new FormData();
        formData.append('file', file);
        if (options?.isPublic) {
            formData.append('is_public', 'true');
        }
        if (options?.owner) {
            formData.append('owner', options.owner);
        }
        if (options?.context) {
            formData.append('context', options.context);
        }
        const propertyId = String(options?.propertyId || '').trim();
        if (propertyId) {
            formData.append('property_id', propertyId);
        }
        const projectId = String(options?.projectId || '').trim();
        if (projectId) {
            formData.append('project_id', projectId);
        }
        const orgId = String(options?.orgId || '').trim();
        if (orgId) {
            formData.append('org_id', orgId);
        }
        return formData;
    }

    private isMultipartFailure(err: any): boolean {
        const code = String(err?.error?.error?.code || err?.error?.code || err?.code || '').trim();
        const message = String(err?.error?.error?.message || err?.error?.message || err?.message || '').trim().toLowerCase();
        if (!message) return false;
        if (message.includes('unexpected end of form')) return true;
        if (code === 'REQUEST_INVALID_MULTIPART') return true;
        return code === 'REQUEST_INVALID_FILE' && message.includes('multipart');
    }

    private uploadViaLegacyFiles(
        file: File,
        options: {
            isPublic?: boolean;
            owner?: 'org' | 'user';
            context?: 'property' | 'project';
            propertyId?: string | null;
            projectId?: string | null;
            orgId?: string | null;
        } = {}
    ): Observable<{ file_id: string }> {
        return this.api.post<any>(`/Files/upload`, this.buildUploadFormData(file, options) as any).pipe(
            map((response) => {
                const fileId = extractUploadFileId(response);
                if (!fileId) {
                    throw new Error('Upload failed: file id was not returned.');
                }
                return { file_id: fileId };
            })
        );
    }

    // ---------------------------------------------
    // vNext helpers
    // ---------------------------------------------

    uploadFile(
        file: File,
        options: {
            isPublic?: boolean;
            owner?: 'org' | 'user';
            context?: 'property' | 'project';
            propertyId?: string | null;
            projectId?: string | null;
            orgId?: string | null;
        } = {}
    ): Observable<{ file_id: string }> {
        return this.api.post<any>(`/API2/files/upload`, this.buildUploadFormData(file, options) as any).pipe(
            map((response) => {
                const fileId = extractUploadFileId(response);
                if (!fileId) {
                    throw new Error('Upload failed: file id was not returned.');
                }
                return { file_id: fileId };
            }),
            catchError((err) => {
                if (!this.isMultipartFailure(err)) {
                    return throwError(() => err);
                }
                return this.uploadViaLegacyFiles(file, options);
            })
        );
    }

    filePublicUrl(fileId: string): string {
        const id = String(fileId || '').trim();
        return id ? `${API_CONFIG.FILES_URL}/public/${encodeURIComponent(id)}` : '';
    }

    fileShareUrl(fileId: string): string {
        const id = String(fileId || '').trim();
        return id ? `${API_CONFIG.FILES_URL}/f/${encodeURIComponent(id)}` : '';
    }

    fileMeta(fileId: string): Observable<{ url?: string | null }> {
        const id = String(fileId || '').trim();
        return this.api.get<any>(`/API2/files/public/${encodeURIComponent(id)}/meta`);
    }

    // ---------------------------------------------
    // Property images (API2)
    // ---------------------------------------------

    attachPropertyImage(
        propertyId: string,
        payload: { file_id: string; alt?: string | null; is_main?: boolean; sort_order?: number | null }
    ): Observable<any> {
        return this.api.post<any>(`/API2/properties/${encodeURIComponent(propertyId)}/images`, payload);
    }

    updatePropertyImage(
        propertyId: string,
        imageId: string,
        payload: { alt?: string | null; is_main?: boolean; sort_order?: number | null }
    ): Observable<any> {
        return this.api.patch<any>(`/API2/properties/${encodeURIComponent(propertyId)}/images/${encodeURIComponent(imageId)}`, payload);
    }

    deletePropertyImage(propertyId: string, imageId: string): Observable<any> {
        return this.api.delete<any>(`/API2/properties/${encodeURIComponent(propertyId)}/images/${encodeURIComponent(imageId)}`);
    }

    // ---------------------------------------------
    // Property children (API2)
    // ---------------------------------------------

    upsertFeatureValue(propertyId: string, featureId: string, value: string): Observable<any> {
        return this.api.put<any>(`/API2/properties/${encodeURIComponent(propertyId)}/features/${encodeURIComponent(featureId)}`, { value });
    }

    deleteFeatureValue(propertyId: string, featureId: string): Observable<any> {
        return this.api.delete<any>(`/API2/properties/${encodeURIComponent(propertyId)}/features/${encodeURIComponent(featureId)}`);
    }

    createLandmark(
        propertyId: string,
        payload: { name: string; distance_meter?: number | null; distance_uom?: string | null; time_minutes?: number | null }
    ): Observable<any> {
        return this.api.post<any>(`/API2/properties/${encodeURIComponent(propertyId)}/landmarks`, payload);
    }

    updateLandmark(
        propertyId: string,
        landmarkId: string,
        payload: { name?: string; distance_meter?: number | null; distance_uom?: string | null; time_minutes?: number | null }
    ): Observable<any> {
        return this.api.patch<any>(`/API2/properties/${encodeURIComponent(propertyId)}/landmarks/${encodeURIComponent(landmarkId)}`, payload);
    }

    deleteLandmark(propertyId: string, landmarkId: string): Observable<any> {
        return this.api.delete<any>(`/API2/properties/${encodeURIComponent(propertyId)}/landmarks/${encodeURIComponent(landmarkId)}`);
    }

	    createPaymentPlan(
	        propertyId: string,
	        payload: {
	            total_period: number;
	            payment_cycle?: number | null;
	            downpayment?: number | null;
	            installment_amount?: number | null;
	            number_of_installments?: number | null;
	            reservation_amount?: number | null;
	            display_price?: number | null;
	            payment_terms?: string | null;
	        }
	    ): Observable<any> {
	        return this.api.post<any>(`/API2/properties/${encodeURIComponent(propertyId)}/payment-plans`, payload);
	    }

	    updatePaymentPlan(
	        propertyId: string,
	        planId: string,
	        payload: Partial<{
	            total_period: number;
	            payment_cycle: number | null;
	            downpayment: number | null;
	            installment_amount: number | null;
	            number_of_installments: number | null;
	            reservation_amount: number | null;
	            display_price: number | null;
	            payment_terms: string | null;
	        }>
	    ): Observable<any> {
	        return this.api.patch<any>(`/API2/properties/${encodeURIComponent(propertyId)}/payment-plans/${encodeURIComponent(planId)}`, payload);
	    }

    deletePaymentPlan(propertyId: string, planId: string): Observable<any> {
        return this.api.delete<any>(`/API2/properties/${encodeURIComponent(propertyId)}/payment-plans/${encodeURIComponent(planId)}`);
    }

    // ---------------------------------------------
    // Property files (brochure / master plan)
    // ---------------------------------------------

    uploadBrochure(propertyId: string, file: File): Observable<{ file_id: string; url: string }> {
        return this.uploadFile(file, { isPublic: true, propertyId }).pipe(
            switchMap(({ file_id }) =>
                this.api.put<any>(`/API2/properties/${encodeURIComponent(propertyId)}`, { brochure_file_id: file_id }).pipe(
                    switchMap(() =>
                        this.fileMeta(file_id).pipe(
                            map((meta) => ({ file_id, url: String(meta?.url || '') }))
                        )
                    )
                )
            )
        );
    }

    uploadMasterPlan(propertyId: string, file: File): Observable<{ file_id: string; url: string }> {
        return this.uploadFile(file, { isPublic: true, propertyId }).pipe(
            switchMap(({ file_id }) =>
                this.api.put<any>(`/API2/properties/${encodeURIComponent(propertyId)}`, { master_plan_file_id: file_id }).pipe(
                    switchMap(() =>
                        this.fileMeta(file_id).pipe(
                            map((meta) => ({ file_id, url: String(meta?.url || '') }))
                        )
                    )
                )
            )
        );
    }

    // ---------------------------------------------
    // Project files (brochure / master plan)
    // ---------------------------------------------

    uploadProjectBrochure(projectId: string, file: File): Observable<{ file_id: string; url: string }> {
        return this.uploadFile(file, { isPublic: true, owner: 'org', context: 'project', projectId }).pipe(
            switchMap(({ file_id }) =>
                this.api.put<any>(`/API2/projects/${encodeURIComponent(projectId)}`, { brochure_file_id: file_id }).pipe(
                    switchMap(() =>
                        this.fileMeta(file_id).pipe(
                            map((meta) => ({ file_id, url: String(meta?.url || this.filePublicUrl(file_id) || '') }))
                        )
                    )
                )
            )
        );
    }

    uploadProjectMasterPlan(projectId: string, file: File): Observable<{ file_id: string; url: string }> {
        return this.uploadFile(file, { isPublic: true, owner: 'org', context: 'project', projectId }).pipe(
            switchMap(({ file_id }) =>
                this.api.put<any>(`/API2/projects/${encodeURIComponent(projectId)}`, { master_plan_file_id: file_id }).pipe(
                    switchMap(() =>
                        this.fileMeta(file_id).pipe(
                            map((meta) => ({ file_id, url: String(meta?.url || this.filePublicUrl(file_id) || '') }))
                        )
                    )
                )
            )
        );
    }
}
